import {  API_URL } from "../../constants/api";
import { BaseQueryFn, FetchArgs, FetchBaseQueryError, createApi, fetchBaseQuery, retry } from "@reduxjs/toolkit/query/react";
import { ACCOUNTS_URL, AUTH_URL, PAYMENTS_URL, PURCHASE_ORDERS_URL, RAFFLES_URL, SELF_VERIFICATION_URL, TICKETS_URL } from "../../constants/urls";
import { ICreationAccountImageDTO, ICreationRaffleDTO, ICreationRaffleImageDTO } from "../interfaces/ICreationRaffle";
import { IRaffle } from "../interfaces/IRaffle";
import { IRaffleStatus } from "../interfaces/IRaffleStatus";
import { ITopPurchaser } from "../interfaces/ITopPurchaser";
import { IWinner } from "../interfaces/IWinner";
import { IGetWinnerConf } from "../interfaces/IGetWinnerConf";
import { ICloseRaffleConf } from "../interfaces/ICloseRaffle";
import { IPaymentCommitTransactionConf, IPaymentInitTransactionConf } from "../interfaces/IPaymentInitTransaction";
import { RootState } from "../types/rootState";
import { IAuthenticationRequest } from "../interfaces/IAuth";
import { IUser } from "../interfaces/IUser";
import { IPaymentCommitResponse } from "../interfaces/IPaymentCommitResponse";
import { IGenerateTicketsRequestConf, IGenerateTicketsResponse, ISendEmailByPurchaserRequest } from "../interfaces/ITickets";
import { IPaymentInitTransactionResponse } from "../interfaces/IPaymentInitTransactionResponse";
import { IGetPurcaseOrdersByStatusArgs, IPurchaseOrder, IRejectPurchaseOrderRequest, ISelfVerificationRequest } from "../interfaces/IPurchaseOrder";
import { IGetTicketsByEmailConf } from "../interfaces/IGetTicketsByEmailConf";
import { logoutAction } from "../slices/auth";
import { saveUserAction } from "../slices/user";
import { enqueueSnackbar } from "notistack";
import { IBlockNumberRequest } from "../interfaces/IBlockNumberRequest";
import { IUpdateBankAccountRequest } from "../../constants/bankAccounts";
import { IReportsData } from "../interfaces/IReportsData";
import { IAccount, IAccountCreateRequest } from "../interfaces/IAccount";


const baseQuery = fetchBaseQuery({
  baseUrl: API_URL,
  prepareHeaders: (headers, { getState, endpoint }) => {
    const auth = (getState() as RootState).authentication
    if (auth.isLoggedIn) {
        headers.set('Authorization', `Bearer ${auth.token}`)
    }
    return headers
},
})


/** Detalle de la petición para incluir en el reporte de error */
export type RequestDetail = {
  fullUrl: string;
  method: string;
  baseUrl: string;
  hostname: string;
  timestamp: string;
  errorStatus: string | number;
  errorMessage: string;
};

function buildRequestDetail(args: string | FetchArgs, err: FetchBaseQueryError): RequestDetail {
  const path = typeof args === "string" ? args : (args?.url ?? "");
  const base = API_URL.replace(/\/$/, "");
  const fullUrl = path?.toString().startsWith("/") ? base + path : base + `/${path}`;
  const method = (typeof args === "object" && args && "method" in args ? (args as FetchArgs).method : "GET") ?? "GET";
  const errAny = err as FetchBaseQueryError & { error?: string };
  const errorMessage = typeof errAny?.error === "string" ? errAny.error : JSON.stringify(errAny?.error ?? "");
  return {
    fullUrl,
    method: String(method).toUpperCase(),
    baseUrl: API_URL,
    hostname: globalThis.location?.hostname ?? "unknown",
    timestamp: new Date().toISOString(),
    errorStatus: err?.status ?? "unknown",
    errorMessage,
  };
}

const baseQueryWithReauth: BaseQueryFn<
string | FetchArgs,
  unknown,
  FetchBaseQueryError & { requestDetail?: RequestDetail }
> = async (args, api, extraOptions) => {
  let result = await baseQuery(args, api, extraOptions);
  const err = result.error;
  if (err) {
    const requestDetail = buildRequestDetail(args, err);
    (result.error as FetchBaseQueryError & { requestDetail?: RequestDetail }).requestDetail = requestDetail;
    if (err.status === "FETCH_ERROR") {
      console.error("[API] Fetch failed (red, CORS o URL incorrecta):", requestDetail);
    }
  }
  if (result.error && result.error.status === 401) {
    api.dispatch(
      logoutAction({
        isLoggedIn: false,
        token: "",
      })
    );
    api.dispatch(saveUserAction(null));
    enqueueSnackbar(`Expiró su token de sesión, por favor logueese nuevamente!`, {
      variant: "error",
      anchorOrigin: {
        horizontal: "left",
        vertical: "top"
      },
    })
    setTimeout(() => {
      window.location.href = "/login";
    }, 2000);
  }
  return result
}

// Errores transitorios del backend (ej. GCS devuelve 503 "backendError" al fijar
// el ACL del objeto). Reintentamos con backoff para que estos fallos pasajeros no
// le lleguen al usuario. Solo se aplica a endpoints idempotentes (ver retryCondition).
const TRANSIENT_STATUSES = [502, 503, 504];
const RETRYABLE_ENDPOINTS = ["createVoucherImage"];

const baseQueryWithRetry = retry(baseQueryWithReauth, {
  retryCondition: (error, _args, { attempt, baseQueryApi }) => {
    if (!RETRYABLE_ENDPOINTS.includes(baseQueryApi.endpoint)) return false;
    if (attempt > 3) return false;
    const status = error?.status;
    return status === "FETCH_ERROR" || (typeof status === "number" && TRANSIENT_STATUSES.includes(status));
  },
});

const api = createApi({
  baseQuery: baseQueryWithRetry,
  
  endpoints: (builder) => ({
    auth: builder.mutation<IUser, IAuthenticationRequest>({
      query: (payload) => ({
        url: `/${AUTH_URL}`,
        method: 'POST',
        body: payload,
        credentials: "include",
      }),
    }),
    createRaffle: builder.mutation<ICreationRaffleDTO, ICreationRaffleDTO>({
      query: (payload: ICreationRaffleDTO) => ({
        url: `/${RAFFLES_URL}`,
        method: 'POST',
        body: payload,
        credentials: "include",
      }),
    }),
    createAccount: builder.mutation<IAccountCreateRequest, IAccountCreateRequest>({
      query: (payload: IAccountCreateRequest) => ({
        url: `/${ACCOUNTS_URL}`,
        method: 'POST',
        body: payload,
        credentials: "include",
      }),
    }),
    closeRaffle: builder.mutation<boolean, ICloseRaffleConf>({
      query: (payload: ICloseRaffleConf) => ({
        url: `/${RAFFLES_URL}/${payload.raffleId}/close`,
        method: 'POST',
        body: payload.conf,
        credentials: "include",
      }),
    }),
    paymentInitTransaction: builder.mutation<IPaymentInitTransactionResponse, IPaymentInitTransactionConf>({
      query: (payload: IPaymentInitTransactionConf) => ({
        url: `/${PAYMENTS_URL}/${payload.raffleId}/initTransaction`,
        method: 'POST',
        body: payload.conf,
        credentials: "include",
      }),
    }),
    paymentCommitTransaction: builder.mutation<IPaymentCommitResponse, IPaymentCommitTransactionConf>({
      query: (payload: IPaymentCommitTransactionConf) => ({
        url: `/${PAYMENTS_URL}/${payload.raffleId}/commit`,
        method: 'PUT',
        body: payload.conf,
        credentials: "include",
      }),
    }),
    updateRaffleBankAccounts: builder.mutation<boolean, IUpdateBankAccountRequest>({
      query: (payload: IUpdateBankAccountRequest) => ({
        url: `/${PAYMENTS_URL}/${payload.raffleId}/bankAccounts`,
        method: 'PUT',
        body: payload.bankAccounts,
        credentials: "include",
      }),
    }),
    rejectPurchaseOrder: builder.mutation<boolean, IRejectPurchaseOrderRequest>({
      query: (payload: IRejectPurchaseOrderRequest) => ({
        url: `/${PURCHASE_ORDERS_URL}/${payload.raffleId}/reject/${payload.purchaseOrderId}`,
        method: 'POST',
        credentials: "include",
      }),
    }),
    cancelTickets: builder.mutation<boolean, IRejectPurchaseOrderRequest>({
      query: (payload: IRejectPurchaseOrderRequest) => ({
        url: `/${PURCHASE_ORDERS_URL}/${payload.raffleId}/cancel/${payload.purchaseOrderId}`,
        method: 'POST',
        credentials: "include",
        body: {
          raffleName: payload.raffleName,
          passwordRequest: payload.passwordRequest,
        }
      }),
    }),
    generateTickets: builder.mutation<IGenerateTicketsResponse, IGenerateTicketsRequestConf>({
      query: (payload: IGenerateTicketsRequestConf) => ({
        url: `/${TICKETS_URL}/${payload.raffleId}/generate`,
        method: 'POST',
        body: payload.config,
        credentials: "include",
      }),
    }),
    sendEmailPurchaser: builder.mutation<IGenerateTicketsResponse, ISendEmailByPurchaserRequest>({
      query: (payload: ISendEmailByPurchaserRequest) => ({
        url: `/${RAFFLES_URL}/${payload.raffleId}/tickets/sendEmail`,
        method: 'POST',
        body: payload,
        credentials: "include",
      }),
    }),
    createRaffleImage: builder.mutation<boolean, ICreationRaffleImageDTO>({
      query: (dto) => {
        const formData = new FormData();
        formData.append('accountId', dto.accountId);
        formData.append('imageName', dto.imageName);
        formData.append('image', dto.imageFile);
    
        return {
          url: `/${RAFFLES_URL}/${dto.raffleId}/images`,
          method: 'PUT',
          body: formData,
          credentials: 'include',
        };
      },
    }),
    createAccountImage: builder.mutation<boolean, ICreationAccountImageDTO>({
      query: (dto) => {
        const formData = new FormData();
        formData.append('accountId', dto.accountId);
        formData.append('imageName', dto.imageName);
        formData.append('image', dto.imageFile);
    
        return {
          url: `/${ACCOUNTS_URL}/${dto.accountId}/images`,
          method: 'PUT',
          body: formData,
          credentials: 'include',
        };
      },
    }),
    createVoucherImage: builder.mutation<boolean, ICreationRaffleImageDTO>({
      query: (dto) => {
        const formData = new FormData();
        formData.append('accountId', dto.accountId);
        formData.append('imageName', dto.imageName);
        formData.append('image', dto.imageFile);
    
        return {
          url: `/${RAFFLES_URL}/${dto.raffleId}/vouchers`,
          method: 'PUT',
          body: formData,
          credentials: 'include',
        };
      },
    }),
    getActiveRaffle: builder.query<IRaffle, string>({
      query: (accountId) => ({
        url: `/${RAFFLES_URL}/active/${accountId}`,
        method: 'GET',
        credentials: "include",
        cache: "no-store",
      }),
      keepUnusedDataFor: 0,
    }),
    getActiveRaffles: builder.query<IRaffle[], string>({
      query: (accountId) => ({
        url: `/${RAFFLES_URL}/active/${accountId}/all`,
        method: 'GET',
        credentials: "include",
        cache: "no-store",
      }),
      keepUnusedDataFor: 0,
    }),
    getRaffleById: builder.query<IRaffle, string>({
      query: (raffleId) => ({
        url: `/${RAFFLES_URL}/public/${raffleId}`,
        method: 'GET',
        credentials: "include",
        cache: "no-store",
      }),
      keepUnusedDataFor: 0,
    }),
    getLastActiveRaffle: builder.query<IRaffle, string>({
      query: (accountId) => ({
        url: `/${RAFFLES_URL}/last/${accountId}`,
        method: 'GET',
        credentials: "include",
      }),
    }),
    activeRaffle: builder.mutation<boolean, string>({
      query: (raffleId) => ({
        url: `/${RAFFLES_URL}/${raffleId}/active`,
        method: 'POST',
        credentials: "include",
      }),
    }),
    blockNumber: builder.mutation<boolean, IBlockNumberRequest>({
      query: (blockNumberReques) => ({
        url: `/${RAFFLES_URL}/${blockNumberReques.raffleId}/block/${blockNumberReques.newNumber}`,
        method: 'POST',
        credentials: "include",
      }),
    }),
    unblockNumber: builder.mutation<boolean, IBlockNumberRequest>({
      query: (blockNumberReques) => ({
        url: `/${RAFFLES_URL}/${blockNumberReques.raffleId}/unblock/${blockNumberReques.newNumber}`,
        method: 'POST',
        credentials: "include",
      }),
    }),
    getBlockedNumbersByRaffleId: builder.query<number[], string>({
      query: (raffleId) => ({
        url: `/${RAFFLES_URL}/${raffleId}/blocked`,
        method: 'GET',
        credentials: "include",
      }),
    }),
    getRaffles: builder.query<IRaffle[], undefined>({
      query: () => ({
        url: `/${RAFFLES_URL}`,
        method: 'GET',
        credentials: "include",
      }),
    }),
    getAccounts: builder.query<IAccount[], undefined>({
      query: () => ({
        url: `/${ACCOUNTS_URL}-list`,
        method: 'GET',
        credentials: "include",
      }),
    }),
    getRaffleWinner: builder.query<IWinner, IGetWinnerConf>({
      query: (conf) => ({
        url: `/${RAFFLES_URL}/${conf.raffleId}/winners/${conf.ticket}`,
        method: 'GET',
        credentials: "include",
      }),
    }),
    getTopPurchasers: builder.query<ITopPurchaser[], string>({
      query: (raffleId) => ({
        url: `/top/${raffleId}`,
        method: 'GET',
        credentials: "include",
      }),
    }),
    getTop10Purchasers: builder.query<ITopPurchaser[], string>({
      query: (raffleId) => ({
        url: `/top-10/${raffleId}`,
        method: 'GET',
        credentials: "include",
      }),
    }),
    getPurchasers: builder.query<ITopPurchaser[], string>({
      query: (raffleId) => ({
        url: `/${RAFFLES_URL}/${raffleId}/purchasers`,
        method: 'GET',
        credentials: "include",
      }),
    }),
    getSoldTickets: builder.query<number[], string>({
      query: (raffleId) => ({
        url: `/${RAFFLES_URL}/${raffleId}/soldTickets`,
        method: 'GET',
        credentials: "include",
      }),
    }),
    getPublicSoldTickets: builder.query<number[], string>({
      query: (raffleId) => ({
        url: `/${RAFFLES_URL}/soldTickets/${raffleId}`,
        method: 'GET',
        credentials: "include",
      }),
    }),
    getTicketsByPurchaserEmail: builder.query<number[], IGetTicketsByEmailConf>({
      query: (conf) => ({
        url: `/${RAFFLES_URL}/${conf.raffleId}/tickets/${conf.email}`,
        method: 'GET',
        credentials: "include",
      }),
    }),
    getRaffleStatus: builder.query<IRaffleStatus, string>({
      query: (raffleId) => ({
        url: `/${RAFFLES_URL}/${raffleId}/status`,
        method: 'GET',
        credentials: "include",
      }),
    }),
    getRaffleReports: builder.query<IReportsData, string>({
      query: (raffleId) => ({
        url: `/${RAFFLES_URL}/${raffleId}/reports`,
        method: 'GET',
        credentials: "include",
      }),
    }),
    getRaffleStatusPublic: builder.query<IRaffleStatus, string>({
      query: (raffleId) => ({
        url: `/${RAFFLES_URL}/status/${raffleId}`,
        method: 'GET',
        credentials: "include",
      }),
    }),
    getAccount: builder.query({
      query: () => ({
        url: `/${ACCOUNTS_URL}`,
        method: 'GET',
        credentials: "include",
      }),
    }),
    getPurchaseOrders: builder.query<IPurchaseOrder[], string>({
      query: (raffleId) => ({
        url: `/${PURCHASE_ORDERS_URL}/${raffleId}`,
        method: 'GET',
        credentials: "include",
      }),
    }),
    getPurchaseOrdersByStatus: builder.query<IPurchaseOrder[], IGetPurcaseOrdersByStatusArgs>({
      query: (args) => ({
        url: `/${PURCHASE_ORDERS_URL}/${args.raffleId}/status/${args.purchaseOrderStatus}`,
        method: 'GET',
        credentials: "include",
      }),
    }),
    getPurchaseOrdersPublic: builder.query<IPurchaseOrder[], ISelfVerificationRequest>({
      query: (payload) => ({
        url: `/${SELF_VERIFICATION_URL}/${payload.raffleId}/${payload.email}`,
        method: 'GET',
        credentials: "include",
      }),
    }),
    regularizeSoldTickets: builder.mutation<{ message: string }, string>({
      query: (raffleId) => ({
        url: `/${RAFFLES_URL}/${raffleId}/soldTickets/regularize`,
        method: 'POST',
        credentials: "include",
      }),
    }),
  })
})

export const { 
  useAuthMutation,
  useCreateRaffleMutation,
  useCreateAccountMutation,
  useCreateAccountImageMutation,
  useGetActiveRaffleQuery,
  useGetActiveRafflesQuery,
  useGetRaffleByIdQuery,
  useGetLastActiveRaffleQuery,
  useGetTop10PurchasersQuery,
  useLazyGetLastActiveRaffleQuery,
  useGetAccountQuery,
  useCreateRaffleImageMutation,
  useGetRaffleStatusQuery,
  useGetRaffleStatusPublicQuery,
  useGetTopPurchasersQuery,
  useGetRafflesQuery,
  useGetAccountsQuery,
  useGetRaffleWinnerQuery,
  useCloseRaffleMutation,
  usePaymentInitTransactionMutation,
  usePaymentCommitTransactionMutation,
  useGenerateTicketsMutation,
  useGetPurchaseOrdersQuery,
  useGetPurchasersQuery,
  useGetTicketsByPurchaserEmailQuery,
  useLazyGetTicketsByPurchaserEmailQuery,
  useCreateVoucherImageMutation,
  useGetSoldTicketsQuery,
  useGetPublicSoldTicketsQuery,
  useRejectPurchaseOrderMutation,
  useLazyGetRaffleWinnerQuery,
  useSendEmailPurchaserMutation,
  useActiveRaffleMutation,
  useGetBlockedNumbersByRaffleIdQuery,
  useBlockNumberMutation,
  useUnblockNumberMutation,
  useUpdateRaffleBankAccountsMutation,
  useGetRaffleReportsQuery,
  useGetPurchaseOrdersPublicQuery,
  useGetPurchaseOrdersByStatusQuery,
  useCancelTicketsMutation,
  useRegularizeSoldTicketsMutation,
} = api;

export default api;
